import React, { useState } from 'react';
import { Card } from '../../SubscriptionAppCard';
import { Plus, Minus, AlertCircle, Lock, CheckCircle } from 'lucide-react';
interface ProductSelectorProps {
  isEditing: boolean;
  formData: any;
  setFormData: (data: any) => void;
  selectedProducts: Record<string, number>;
  updateProductQuantity: (productId: string, delta: number) => void;
  availableProducts: Array<{
    id: string;
    name: string;
    price: number;
  }>;
  currentTotalBottles: number;
  targetBottles: number;
  isBottleCountCorrect: boolean;
  subscriptionItems: Array<{
    name: string;
    quantity: number;
  }>;
  subtotal: number;
  discountPercent: number;
  discountAmount: number;
  total: number;
  subscriptionTitle: string;
  'data-id'?: string;
}
// Mock fixed box definitions (in real app, this would come from Club Builder)
const FIXED_BOXES = [{
  id: 'premium-red',
  name: 'Premium Red Club',
  bottleCount: '6',
  frequency: 'Every 2 Months',
  wines: [{
    name: '2019 Penfolds Bin 389',
    quantity: 2,
    price: 89.99
  }, {
    name: '2020 Henschke Mount Edelstone',
    quantity: 2,
    price: 125.0
  }, {
    name: '2018 Torbreck The Struie',
    quantity: 2,
    price: 65.0
  }]
}, {
  id: 'mixed-dozen',
  name: 'Mixed Dozen',
  bottleCount: '12',
  frequency: 'Every 3 Months',
  wines: [{
    name: '2021 Shaw + Smith Sauvignon Blanc',
    quantity: 3,
    price: 32.0
  }, {
    name: '2020 Penfolds Bin 28',
    quantity: 3,
    price: 45.0
  }, {
    name: '2019 Yalumba The Octavius',
    quantity: 3,
    price: 95.0
  }, {
    name: '2020 Leeuwin Estate Art Series Chardonnay',
    quantity: 3,
    price: 78.0
  }]
}, {
  id: 'cellar-selection',
  name: 'Cellar Selection',
  bottleCount: '6',
  frequency: 'Every 6 Months',
  wines: [{
    name: '2015 Penfolds Grange',
    quantity: 2,
    price: 850.0
  }, {
    name: '2016 Henschke Hill of Grace',
    quantity: 2,
    price: 795.0
  }, {
    name: '2017 Cullen Diana Madeline',
    quantity: 2,
    price: 165.0
  }]
}, {
  id: 'sparkling-club',
  name: 'Sparkling Club',
  bottleCount: '6',
  frequency: 'Every Month',
  wines: [{
    name: 'NV Chandon Brut',
    quantity: 3,
    price: 28.0
  }, {
    name: 'NV Jansz Premium Cuvée',
    quantity: 3,
    price: 35.0
  }]
}];
export function ProductSelector({
  isEditing,
  formData,
  setFormData,
  selectedProducts,
  updateProductQuantity,
  availableProducts,
  currentTotalBottles,
  targetBottles,
  isBottleCountCorrect,
  subscriptionItems,
  subtotal,
  discountPercent,
  discountAmount,
  total,
  subscriptionTitle,
  'data-id': dataId
}: ProductSelectorProps) {
  const [selectedFixedBox, setSelectedFixedBox] = useState<string>('');
  // Helper function to get product price by name
  const getProductPrice = (productName: string): number => {
    // Try exact match first
    const exactMatch = availableProducts.find(p => p.name === productName);
    if (exactMatch) return exactMatch.price;
    // Try partial match (subscription items may have SKU prefix like '25FID6 – 2025 Fiano')
    const partialMatch = availableProducts.find(p => productName.includes(p.name) || p.name.includes(productName));
    if (partialMatch) return partialMatch.price;
    // Fallback: keyword-based matching
    if (productName.includes('Fiano')) return 35.0;
    if (productName.includes('Shiraz')) return 45.0;
    if (productName.includes('Chardonnay')) return 40.0;
    if (productName.includes('Pinot Noir')) return 42.0;
    if (productName.includes('Sauvignon Blanc')) return 38.0;
    if (productName.includes('Cabernet Sauvignon')) return 55.0;
    if (productName.includes('Rosé') || productName.includes('Rose')) return 32.0;
    if (productName.includes('Tempranillo')) return 36.0;
    if (productName.includes('Pinot Grigio')) return 34.0;
    if (productName.includes('Viognier')) return 39.0;
    if (productName.includes('Malbec')) return 44.0;
    if (productName.includes('GSM')) return 42.0;
    return 38.0;
  };
  // Tax rate (10% GST)
  const TAX_RATE = 0.1;
  // Calculate tax and price minus tax
  const calculatePricing = (price: number, quantity: number) => {
    const totalPrice = price * quantity;
    const priceMinusTax = totalPrice / (1 + TAX_RATE);
    const tax = totalPrice - priceMinusTax;
    return {
      totalPrice,
      priceMinusTax,
      tax
    };
  };
  // Calculate total tax for all items
  const calculateTotalTax = () => {
    return subscriptionItems.reduce((totalTax, item) => {
      const price = getProductPrice(item.name);
      const pricing = calculatePricing(price, item.quantity);
      return totalTax + pricing.tax;
    }, 0);
  };
  const totalTax = calculateTotalTax();
  const subtotalExTax = subtotal / (1 + TAX_RATE);
  // Get selected fixed box data
  const fixedBoxData = FIXED_BOXES.find(box => box.id === selectedFixedBox);
  // Handle preference change
  const handlePreferenceChange = (newPreference: string) => {
    if (formData.preference === 'Custom' && newPreference === 'Fixed') {
      // Switching to Fixed - clear custom selection
      setFormData({
        ...formData,
        preference: newPreference
      });
      setSelectedFixedBox('');
    } else if (formData.preference === 'Fixed' && newPreference === 'Custom') {
      // Switching back to Custom - optionally show confirmation
      const confirmSwitch = window.confirm('Switching to custom will clear current selection. Continue?');
      if (confirmSwitch) {
        setFormData({
          ...formData,
          preference: newPreference
        });
        setSelectedFixedBox('');
      }
    } else {
      setFormData({
        ...formData,
        preference: newPreference
      });
    }
  };
  // Handle fixed box selection
  const handleFixedBoxChange = (boxId: string) => {
    setSelectedFixedBox(boxId);
    const box = FIXED_BOXES.find(b => b.id === boxId);
    if (box) {
      setFormData({
        ...formData,
        bottleCount: box.bottleCount,
        frequency: box.frequency
      });
    }
  };
  const isFixedMode = formData.preference === 'Fixed';
  const isFixedBoxSelected = isFixedMode && selectedFixedBox && fixedBoxData;
  return <Card padding="none" data-id="element-3075">
      {/* Header */}
      <div className="px-6 py-5 border-b border-[#E2E8F0] bg-gradient-to-r from-[#F8FAFC] to-white" data-id="element-3076">
        <div className="flex items-center justify-between" data-id="element-3077">
          <h3 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-3078">
            Current Selection
          </h3>
          <div className="bg-[#E0F2FE] text-[#0EA5E9] px-4 py-2 rounded-md text-[13px] font-medium" data-id="element-3079">
            {isFixedBoxSelected ? `${fixedBoxData.bottleCount} Bottles ${fixedBoxData.frequency}` : subscriptionTitle}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 bg-white" data-id="element-3080">
        {isEditing ? <div className="flex gap-8" data-id="element-3081">
            {/* Left Column - 40% - Dropdowns and Alert */}
            <div className="w-[40%] space-y-6" data-id="element-3082">
              {/* Preference Dropdown */}
              <div data-id="element-3083">
                <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-3084">
                  Preference
                </label>
                <select className="w-full h-10 px-3 pr-10 border border-[#CBD5E1] rounded-md bg-white text-[14px] text-[#0F172A] focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20 outline-none transition-all duration-200 appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23475569%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-[length:20px_20px] bg-[right_12px_center] bg-no-repeat hover:border-[#94A3B8] cursor-pointer" value={formData.preference} onChange={e => handlePreferenceChange(e.target.value)} data-id="element-3085">
                  <option value="Custom" data-id="element-3086">Custom</option>
                  <option value="Fixed" data-id="element-3087">Fixed</option>
                </select>
              </div>

              {/* Fixed Box Selector - Only show when Fixed preference is selected */}
              {isFixedMode && <div data-id="element-3088">
                  <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-3089">
                    Select Club
                  </label>
                  <select className="w-full h-10 px-3 pr-10 border border-[#CBD5E1] rounded-md bg-white text-[14px] text-[#0F172A] focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20 outline-none transition-all duration-200 appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23475569%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-[length:20px_20px] bg-[right_12px_center] bg-no-repeat hover:border-[#94A3B8] cursor-pointer" value={selectedFixedBox} onChange={e => handleFixedBoxChange(e.target.value)} data-id="element-3090">
                    <option value="" data-id="element-3091">Choose a club...</option>
                    {FIXED_BOXES.map(box => <option key={box.id} value={box.id} data-id="element-3092">
                        {box.name}
                      </option>)}
                  </select>
                </div>}

              {/* Bottle Count Dropdown */}
              <div data-id="element-3093">
                <label className="block text-[12px] font-medium text-[#475569] mb-1.5 flex items-center gap-2" data-id="element-3094">
                  Bottle Count
                  {isFixedBoxSelected && <Lock className="w-3 h-3 text-[#94A3B8]" data-id="element-3095" />}
                </label>
                <select disabled={isFixedBoxSelected} className={`w-full h-10 px-3 pr-10 border rounded-md text-[14px] outline-none transition-all duration-200 appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23475569%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-[length:20px_20px] bg-[right_12px_center] bg-no-repeat ${isFixedBoxSelected ? 'bg-[#F1F5F9] border-[#E2E8F0] text-[#64748B] cursor-not-allowed' : 'bg-white border-[#CBD5E1] text-[#0F172A] hover:border-[#94A3B8] cursor-pointer focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20'}`} value={formData.bottleCount} onChange={e => setFormData({
              ...formData,
              bottleCount: e.target.value
            })} data-id="element-3096">
                  <option value="3" data-id="element-3097">3 Bottles</option>
                  <option value="6" data-id="element-3098">6 Bottles</option>
                  <option value="12" data-id="element-3099">12 Bottles</option>
                </select>
                {isFixedBoxSelected && <p className="text-[12px] text-[#64748B] mt-1" data-id="element-3100">
                    Defined by club settings
                  </p>}
              </div>

              {/* Frequency Dropdown */}
              <div data-id="element-3101">
                <label className="block text-[12px] font-medium text-[#475569] mb-1.5 flex items-center gap-2" data-id="element-3102">
                  Frequency
                  {isFixedBoxSelected && <Lock className="w-3 h-3 text-[#94A3B8]" data-id="element-3103" />}
                </label>
                <select disabled={isFixedBoxSelected} className={`w-full h-10 px-3 pr-10 border rounded-md text-[14px] outline-none transition-all duration-200 appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23475569%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-[length:20px_20px] bg-[right_12px_center] bg-no-repeat ${isFixedBoxSelected ? 'bg-[#F1F5F9] border-[#E2E8F0] text-[#64748B] cursor-not-allowed' : 'bg-white border-[#CBD5E1] text-[#0F172A] hover:border-[#94A3B8] cursor-pointer focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20'}`} value={formData.frequency} onChange={e => setFormData({
              ...formData,
              frequency: e.target.value
            })} data-id="element-3104">
                  <option value="Every Month" data-id="element-3105">Every Month</option>
                  <option value="Every 2 Months" data-id="element-3106">Every 2 Months</option>
                  <option value="Every 3 Months" data-id="element-3107">Every 3 Months</option>
                  <option value="Every 6 Months" data-id="element-3108">Every 6 Months</option>
                </select>
                {isFixedBoxSelected && <p className="text-[12px] text-[#64748B] mt-1" data-id="element-3109">
                    Defined by club settings
                  </p>}
              </div>

              {/* Validation/Confirmation Alert */}
              {isFixedBoxSelected ?
          // Fixed Box Confirmation
          <div className="p-4 rounded-md border bg-[#D1FAE5] border-[#10B981] transition-all duration-200" data-id="element-3110">
                  <div className="flex items-start gap-3" data-id="element-3111">
                    <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5 text-[#10B981]" data-id="element-3112" />
                    <div data-id="element-3113">
                      <p className="text-[14px] font-semibold text-[#059669]" data-id="element-3114">
                        Club selection applied
                      </p>
                      <p className="text-[13px] mt-1 text-[#047857]" data-id="element-3115">
                        {fixedBoxData.bottleCount} bottles{' '}
                        {fixedBoxData.frequency.toLowerCase()}
                      </p>
                    </div>
                  </div>
                </div> :
          // Custom Bottle Counter Alert
          <div className={`p-4 rounded-md border transition-all duration-200 ${isBottleCountCorrect ? 'bg-[#D1FAE5] border-[#10B981]' : 'bg-[#FEF3C7] border-[#F59E0B]'}`} data-id="element-3116">
                  <div className="flex items-start gap-3" data-id="element-3117">
                    <AlertCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isBottleCountCorrect ? 'text-[#10B981]' : 'text-[#F59E0B]'}`} data-id="element-3118" />
                    <div data-id="element-3119">
                      <p className={`text-[14px] font-semibold ${isBottleCountCorrect ? 'text-[#059669]' : 'text-[#D97706]'}`} data-id="element-3120">
                        {isBottleCountCorrect ? 'Bottle count is correct' : 'Adjust bottle selection'}
                      </p>
                      <p className={`text-[13px] mt-1 ${isBottleCountCorrect ? 'text-[#047857]' : 'text-[#B45309]'}`} data-id="element-3121">
                        {currentTotalBottles} of {targetBottles} bottles
                        selected
                      </p>
                    </div>
                  </div>
                </div>}
            </div>

            {/* Right Column - 60% - Wine List */}
            <div className="w-[60%]" data-id="element-3122">
              <label className="block text-[12px] font-medium text-[#475569] mb-3" data-id="element-3123">
                {isFixedBoxSelected ? 'Included Wines' : 'Select Wines'}
              </label>
              <div className={`border border-[#CBD5E1] rounded-md max-h-[420px] overflow-y-auto shadow-sm ${isFixedBoxSelected ? 'bg-[#F8FAFC]' : 'bg-white'}`} data-id="element-3124">
                {isFixedBoxSelected ?
            // Fixed Box - Read-only wine list
            <>
                    {fixedBoxData.wines.map((wine, index) => <div key={index} className="flex items-center justify-between px-4 py-3 border-b border-[#E2E8F0] last:border-b-0 bg-[#F8FAFC]" data-id="element-3125">
                        <div className="flex-1 min-w-0 pr-4" data-id="element-3126">
                          <p className="text-[14px] font-medium text-[#0F172A] truncate" data-id="element-3127">
                            {wine.name}
                          </p>
                          <p className="text-[13px] text-[#94A3B8] mt-0.5" data-id="element-3128">
                            ${wine.price.toFixed(2)}
                          </p>
                        </div>
                        <div className="flex items-center" data-id="element-3129">
                          <span className="inline-flex items-center justify-center min-w-[48px] h-8 px-3 bg-[#E2E8F0] text-[#475569] font-semibold rounded-md text-[14px]" data-id="element-3130">
                            ×{wine.quantity}
                          </span>
                        </div>
                      </div>)}
                    <div className="px-4 py-3 bg-[#F1F5F9] border-t border-[#E2E8F0]" data-id="element-3131">
                      <p className="text-[12px] text-[#64748B] flex items-center gap-2" data-id="element-3132">
                        <Lock className="w-3 h-3" data-id="element-3133" />
                        Wine selection is managed by club settings
                      </p>
                    </div>
                  </> :
            // Custom - Editable wine list
            availableProducts.map(product => {
              const quantity = selectedProducts[product.id] || 0;
              const isSelected = quantity > 0;
              return <div key={product.id} className={`flex items-center justify-between px-4 py-3 border-b border-[#E2E8F0] last:border-b-0 transition-all duration-200 ${isSelected ? 'bg-[#E0F2FE]' : 'bg-white hover:bg-[#F8FAFC]'}`} data-id="element-3134">
                        <div className="flex-1 min-w-0 pr-4" data-id="element-3135">
                          <p className="text-[14px] font-medium text-[#0F172A] truncate" data-id="element-3136">
                            {product.name}
                          </p>
                          <p className="text-[13px] text-[#94A3B8] mt-0.5" data-id="element-3137">
                            ${product.price.toFixed(2)}
                          </p>
                        </div>
                        <div className="flex items-center gap-3" data-id="element-3138">
                          <button onClick={() => updateProductQuantity(product.id, -1)} disabled={quantity === 0} className="w-10 h-10 flex items-center justify-center rounded-md border border-[#CBD5E1] bg-white text-[#475569] hover:bg-[#F1F5F9] hover:border-[#94A3B8] disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 active:scale-95" aria-label="Decrease quantity" data-id="element-3139">
                            <Minus className="w-4 h-4" data-id="element-3140" />
                          </button>
                          <span className="w-10 text-center text-[14px] font-semibold text-[#0F172A]" data-id="element-3141">
                            {quantity}
                          </span>
                          <button onClick={() => updateProductQuantity(product.id, 1)} className="w-10 h-10 flex items-center justify-center rounded-md border border-[#0EA5E9] bg-[#0EA5E9] text-white hover:bg-[#0284C7] hover:border-[#0284C7] transition-all duration-200 active:scale-95 shadow-sm" aria-label="Increase quantity" data-id="element-3142">
                            <Plus className="w-4 h-4" data-id="element-3143" />
                          </button>
                        </div>
                      </div>;
            })}
              </div>
            </div>
          </div> :
      // View Mode - Table Layout
      <div data-id="element-3144">
            {/* Table Header */}
            <div className="bg-[#F1F5F9] border-b border-[#E2E8F0] rounded-t-md" data-id="element-3145">
              <div className="grid grid-cols-12 gap-4 px-4 py-3" data-id="element-3146">
                <div className="col-span-6" data-id="element-3147">
                  <span className="text-[12px] font-semibold text-[#64748B] uppercase tracking-[0.5px]" data-id="element-3148">
                    Item
                  </span>
                </div>
                <div className="col-span-2 text-right" data-id="element-3149">
                  <span className="text-[12px] font-semibold text-[#64748B] uppercase tracking-[0.5px]" data-id="element-3150">
                    Price
                  </span>
                </div>
                <div className="col-span-2 text-center" data-id="element-3151">
                  <span className="text-[12px] font-semibold text-[#64748B] uppercase tracking-[0.5px]" data-id="element-3152">
                    Qty
                  </span>
                </div>
                <div className="col-span-2 text-right" data-id="element-3153">
                  <span className="text-[12px] font-semibold text-[#64748B] uppercase tracking-[0.5px]" data-id="element-3154">
                    Total
                  </span>
                </div>
              </div>
            </div>

            {/* Table Body */}
            <div data-id="element-3155">
              {subscriptionItems.map((item, index) => {
            const price = getProductPrice(item.name);
            const itemTotal = price * item.quantity;
            return <div key={index} className={`grid grid-cols-12 gap-4 px-4 py-4 ${index !== subscriptionItems.length - 1 ? 'border-b border-[#E2E8F0]' : ''} hover:bg-[#F8FAFC] transition-colors duration-200`} data-id="element-3156">
                    <div className="col-span-6 flex items-center" data-id="element-3157">
                      <p className="text-[14px] text-[#0F172A] font-medium" data-id="element-3158">
                        {item.name}
                      </p>
                    </div>
                    <div className="col-span-2 text-right flex items-center justify-end" data-id="element-3159">
                      <span className="text-[14px] text-[#475569]" data-id="element-3160">
                        ${price.toFixed(2)}
                      </span>
                    </div>
                    <div className="col-span-2 text-center flex items-center justify-center" data-id="element-3161">
                      <span className="inline-flex items-center justify-center min-w-[32px] h-7 px-2.5 bg-[#F1F5F9] text-[#0F172A] font-semibold rounded-md text-[14px]" data-id="element-3162">
                        {item.quantity}
                      </span>
                    </div>
                    <div className="col-span-2 text-right flex items-center justify-end" data-id="element-3163">
                      <span className="text-[14px] text-[#0F172A] font-semibold" data-id="element-3164">
                        ${itemTotal.toFixed(2)}
                      </span>
                    </div>
                  </div>;
          })}
            </div>

            {/* Totals Section */}
            <div className="mt-6 pt-6 border-t border-[#E2E8F0]" data-id="element-3165">
              <div className="flex justify-end" data-id="element-3166">
                <div className="w-80 space-y-3" data-id="element-3167">
                  <div className="flex justify-between items-center" data-id="element-3168">
                    <span className="text-[14px] text-[#475569]" data-id="element-3169">Subtotal</span>
                    <span className="text-[14px] text-[#0F172A] font-medium" data-id="element-3170">
                      ${subtotal.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center" data-id="element-3171">
                    <span className="text-[14px] text-[#10B981] font-medium" data-id="element-3172">
                      Savings ({discountPercent}%)
                    </span>
                    <span className="text-[14px] text-[#10B981] font-semibold" data-id="element-3173">
                      -${discountAmount.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center pt-3 border-t border-[#CBD5E1]" data-id="element-3174">
                    <span className="text-[16px] text-[#0F172A] font-semibold" data-id="element-3175">
                      Total
                    </span>
                    <span className="text-[16px] text-[#0EA5E9] font-semibold" data-id="element-3176">
                      ${total.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>}
      </div>
    </Card>;
}